/**
 * Numbers → Spanish words, following the Real Academia Española and ASALE.
 *
 * Sources (the rae.es pages refuse automated requests, so the rules were checked against
 * the academy's own wording as quoted by the secondary sources listed, then against a
 * word-by-word parser in the tests):
 *
 * - Diccionario panhispánico de dudas (DPD), entries "cardinales", "uno", "cien(to)",
 *   "mil", "billón", "millardo" — the cardinal rules below.
 * - Ortografía de la lengua española (RAE-ASALE, 2010), "Ortografía de los numerales":
 *   16–19 and 21–29 in one word, the other tens joined with "y".
 * - Real Academia Española, "Buen uso del español", "Los ordinales", and the DPD entry
 *   "ordinales" — the ordinal forms, one-word versus two-word spellings, apocope.
 *
 * Rules implemented:
 * - 0 cero; 16–19 dieciséis…diecinueve and 21–29 veintiuno…veintinueve in ONE word (with
 *   the accents veintidós, veintitrés, veintiséis); 31–99 "treinta y uno".
 * - "cien" alone, before a noun and before "mil"/"millones"; "ciento" before more digits.
 * - 200–900 take -as with a feminine noun, and keep agreeing across "mil": "doscientas mil
 *   personas". Before "millón" they stay masculine because "millón" is a masculine noun.
 * - "uno" is a counting word; before a masculine noun it is "un" (veintiún, treinta y un,
 *   ciento un); before a feminine noun "una". Before "mil" the apocope is obligatory with
 *   a masculine noun and optional with a feminine one (veintiún mil / veintiuna mil).
 * - 1000 is "mil", never "un mil"; 10^6 is "un millón" (a noun: "un millón de personas",
 *   "un millón doscientas mil personas"); the scale is the long one: mil millones = 10^9,
 *   un billón = 10^12, un trillón = 10^18.
 */

import { isWholeDigits, splitGroups } from "./number-input";

export type SpanishNoun = "none" | "masculine" | "feminine";
export type FemininePlacement = "apocope" | "full";

export interface SpanishCardinalOptions {
  /** The noun the number counts: none (just counting), masculine or feminine. */
  noun: SpanishNoun;
  /** Feminine noun after "uno" before "mil": "veintiún mil" (apocope) or "veintiuna mil". */
  femBeforeMil: FemininePlacement;
}

export type SpanishNote =
  | "apocope"
  | "apocopeBeforeMil"
  | "cien"
  | "ciento"
  | "feminineHundreds"
  | "de"
  | "longScale";

export interface SpanishCardinal {
  words: string;
  /** True when a noun after the number needs "de" ("un millón de personas"). */
  needsDe: boolean;
  /** The other correct spelling(s) the academy accepts for the same number. */
  alternatives: string[];
  notes: SpanishNote[];
}

const UNITS = [
  "",
  "uno",
  "dos",
  "tres",
  "cuatro",
  "cinco",
  "seis",
  "siete",
  "ocho",
  "nueve",
] as const;

const TEENS = [
  "diez",
  "once",
  "doce",
  "trece",
  "catorce",
  "quince",
  "dieciséis",
  "diecisiete",
  "dieciocho",
  "diecinueve",
] as const;

const TWENTIES = [
  "veinte",
  "veintiuno",
  "veintidós",
  "veintitrés",
  "veinticuatro",
  "veinticinco",
  "veintiséis",
  "veintisiete",
  "veintiocho",
  "veintinueve",
] as const;

const TENS = [
  "",
  "",
  "",
  "treinta",
  "cuarenta",
  "cincuenta",
  "sesenta",
  "setenta",
  "ochenta",
  "noventa",
] as const;

const HUNDREDS = [
  "",
  "ciento",
  "doscientos",
  "trescientos",
  "cuatrocientos",
  "quinientos",
  "seiscientos",
  "setecientos",
  "ochocientos",
  "novecientos",
] as const;

/** Names of the 10^6, 10^12, 10^18 and 10^24 groups: singular, plural. */
const SCALES: readonly (readonly [string, string])[] = [
  ["", ""],
  ["millón", "millones"],
  ["billón", "billones"],
  ["trillón", "trillones"],
];

type UnoForm = "uno" | "un" | "una";

/** The word for a final 1 in each form, with the accent veintiún takes. */
function unoWord(form: UnoForm): string {
  return form;
}

function twentyNine(n: number, form: UnoForm): string {
  if (n === 21) {
    if (form === "un") {
      return "veintiún";
    }
    return form === "una" ? "veintiuna" : "veintiuno";
  }
  return TWENTIES[n - 20];
}

function underHundred(n: number, form: UnoForm): string {
  if (n < 10) {
    return n === 1 ? unoWord(form) : UNITS[n];
  }
  if (n < 20) {
    return TEENS[n - 10];
  }
  if (n < 30) {
    return twentyNine(n, form);
  }
  const tens = TENS[Math.floor(n / 10)];
  const unit = n % 10;
  if (unit === 0) {
    return tens;
  }
  return `${tens} y ${unit === 1 ? unoWord(form) : UNITS[unit]}`;
}

function hundredWord(h: number, feminine: boolean): string {
  const word = HUNDREDS[h];
  return feminine && h >= 2 ? `${word.slice(0, -2)}as` : word;
}

/** 0–999 as words. Zero gives "". */
function underThousand(n: number, form: UnoForm, feminine: boolean): string {
  const h = Math.floor(n / 100);
  const rest = n % 100;
  const parts: string[] = [];
  if (h > 0) {
    parts.push(h === 1 && rest === 0 ? "cien" : hundredWord(h, feminine));
  }
  if (rest > 0) {
    parts.push(underHundred(rest, form));
  }
  return parts.join(" ");
}

interface GroupContext {
  /** True for the millones/billones/trillones groups, whose noun is masculine. */
  scaled: boolean;
  noun: SpanishNoun;
  femBeforeMil: FemininePlacement;
}

function formBeforeMil(ctx: GroupContext): UnoForm {
  if (!ctx.scaled && ctx.noun === "feminine" && ctx.femBeforeMil === "full") {
    return "una";
  }
  return "un";
}

function formAtEnd(ctx: GroupContext): UnoForm {
  if (ctx.scaled) {
    return "un";
  }
  if (ctx.noun === "masculine") {
    return "un";
  }
  return ctx.noun === "feminine" ? "una" : "uno";
}

/** 1–999,999 as words, agreeing with the noun the group sits before. */
function groupWords(v: number, ctx: GroupContext): string {
  const thousands = Math.floor(v / 1000);
  const rest = v % 1000;
  const feminine = !ctx.scaled && ctx.noun === "feminine";
  const parts: string[] = [];
  if (thousands === 1) {
    parts.push("mil");
  } else if (thousands > 1) {
    parts.push(`${underThousand(thousands, formBeforeMil(ctx), feminine)} mil`);
  }
  if (rest > 0) {
    parts.push(underThousand(rest, formAtEnd(ctx), feminine));
  }
  return parts.join(" ");
}

const NON_ZERO_DIGIT = /[1-9]/;
const RE_CIEN = /\bcien\b/;
const RE_CIENTO = /\bciento\b/;
const RE_FEMININE_HUNDREDS = /\b\w+cientas\b/;

/** A 1 that is not part of 11: the digit that makes "uno" appear. */
function endsInUno(x: number): boolean {
  return x % 10 === 1 && x % 100 !== 11;
}

function usesApocopeBeforeScale(groups: number[]): boolean {
  return groups.some((v, i) => {
    const thousands = Math.floor(v / 1000);
    const unoBeforeMil = thousands > 1 && endsInUno(thousands);
    if (i === 0) {
      return unoBeforeMil;
    }
    return v === 1 || unoBeforeMil || endsInUno(v % 1000);
  });
}

function collectNotes(
  digits: string,
  words: string,
  options: SpanishCardinalOptions
): SpanishNote[] {
  const notes: SpanishNote[] = [];
  const groups = splitGroups(digits, 6);
  if (options.noun === "masculine" && endsInUno(Number(digits.slice(-3)))) {
    notes.push("apocope");
  }
  if (usesApocopeBeforeScale(groups)) {
    notes.push("apocopeBeforeMil");
  }
  if (RE_CIEN.test(words)) {
    notes.push("cien");
  }
  if (RE_CIENTO.test(words)) {
    notes.push("ciento");
  }
  if (options.noun === "feminine" && RE_FEMININE_HUNDREDS.test(words)) {
    notes.push("feminineHundreds");
  }
  if (groups[0] === 0) {
    notes.push("de");
  }
  if (digits.length >= 10) {
    notes.push("longScale");
  }
  return notes;
}

/** The words alone — the part `spanishCardinal` also needs for its alternatives. */
function buildCardinal(
  digits: string,
  options: SpanishCardinalOptions
): string {
  if (digits === "0") {
    return "cero";
  }
  const groups = splitGroups(digits, 6);
  const parts: string[] = [];
  for (let i = groups.length - 1; i >= 0; i--) {
    const v = groups[i];
    if (v === 0) {
      continue;
    }
    const ctx: GroupContext = {
      scaled: i > 0,
      noun: options.noun,
      femBeforeMil: options.femBeforeMil,
    };
    if (i === 0) {
      parts.push(groupWords(v, ctx));
    } else if (v === 1) {
      parts.push(`un ${SCALES[i][0]}`);
    } else {
      parts.push(`${groupWords(v, ctx)} ${SCALES[i][1]}`);
    }
  }
  return parts.join(" ");
}

/** The cardinal for a whole number from 0 to 10^24 − 1, given as decimal digits. */
export function spanishCardinal(
  digits: string,
  options: SpanishCardinalOptions
): SpanishCardinal {
  if (!isWholeDigits(digits)) {
    throw new RangeError("spanishCardinal: out of range");
  }
  const words = buildCardinal(digits, options);
  const alternatives: string[] = [];
  if (options.noun === "feminine") {
    const other = buildCardinal(digits, {
      noun: "feminine",
      femBeforeMil: options.femBeforeMil === "apocope" ? "full" : "apocope",
    });
    if (other !== words) {
      alternatives.push(other);
    }
  }
  return {
    words,
    needsDe: digits !== "0" && splitGroups(digits, 6)[0] === 0,
    alternatives,
    notes: digits === "0" ? [] : collectNotes(digits, words, options),
  };
}

/** The decimal digits read one by one: "tres coma uno cuatro". */
export function spanishDecimalDigits(fraction: string): string {
  return [...fraction]
    .map((d) => (d === "0" ? "cero" : UNITS[Number(d)]))
    .join(" ");
}

export type DecimalMark = "coma" | "punto";

export interface SpanishNumber {
  words: string;
  alternatives: string[];
  notes: SpanishNote[];
  needsDe: boolean;
}

/** A signed, possibly decimal number: "menos tres coma catorce". */
export function spanishNumber(
  negative: boolean,
  integer: string,
  fraction: string,
  options: SpanishCardinalOptions & { mark: DecimalMark }
): SpanishNumber {
  const hasFraction = fraction !== "" && NON_ZERO_DIGIT.test(fraction);
  const whole = spanishCardinal(
    integer,
    hasFraction ? { ...options, noun: "none" } : options
  );
  let words = whole.words;
  if (hasFraction) {
    words = `${words} ${options.mark} ${spanishDecimalDigits(fraction)}`;
  }
  if (negative) {
    words = `menos ${words}`;
  }
  return {
    words,
    alternatives: hasFraction
      ? []
      : whole.alternatives.map((a) => (negative ? `menos ${a}` : a)),
    notes: hasFraction ? [] : whole.notes,
    needsDe: !hasFraction && whole.needsDe,
  };
}

// ---------------------------------------------------------------------------
// Ordinals

export type OrdinalGender = "masculine" | "feminine";
export type OrdinalSpelling = "oneWord" | "twoWords";

export interface SpanishOrdinalOptions {
  gender: OrdinalGender;
  /** Before a masculine singular noun primero → primer, tercero → tercer. */
  beforeNoun: boolean;
  /** 13–19 and 21–29: "decimotercero" (RAE's first choice) or "décimo tercero". */
  spelling: OrdinalSpelling;
}

export interface SpanishOrdinal {
  words: string;
  abbreviation: string;
  alternatives: string[];
}

export const MAX_ORDINAL = 1000;

const ORD_UNITS = [
  "",
  "primero",
  "segundo",
  "tercero",
  "cuarto",
  "quinto",
  "sexto",
  "séptimo",
  "octavo",
  "noveno",
] as const;

const ORD_TENS = [
  "",
  "décimo",
  "vigésimo",
  "trigésimo",
  "cuadragésimo",
  "quincuagésimo",
  "sexagésimo",
  "septuagésimo",
  "octogésimo",
  "nonagésimo",
] as const;

const ORD_HUNDREDS = [
  "",
  "centésimo",
  "ducentésimo",
  "tricentésimo",
  "cuadringentésimo",
  "quingentésimo",
  "sexcentésimo",
  "septingentésimo",
  "octingentésimo",
  "noningentésimo",
] as const;

/** Masculine ordinal words for 1–99, with the joined or separated spelling chosen. */
function ordinalUnderHundred(n: number, spelling: OrdinalSpelling): string[] {
  if (n < 10) {
    return [ORD_UNITS[n]];
  }
  if (n === 10) {
    return [ORD_TENS[1]];
  }
  if (n === 11) {
    return ["undécimo"];
  }
  if (n === 12) {
    return ["duodécimo"];
  }
  const tens = Math.floor(n / 10);
  const unit = n % 10;
  if (unit === 0) {
    return [ORD_TENS[tens]];
  }
  if ((tens === 1 || tens === 2) && spelling === "oneWord") {
    return [joinedOrdinal(tens, unit)];
  }
  return [ORD_TENS[tens], ORD_UNITS[unit]];
}

/** décimo + tercero = decimotercero; vigésimo + octavo = vigesimoctavo. */
function joinedOrdinal(tens: number, unit: number): string {
  const stem = tens === 1 ? "decimo" : "vigesimo";
  const word = ORD_UNITS[unit];
  return unit === 8 ? `${stem.slice(0, -1)}${word}` : `${stem}${word}`;
}

function ordinalWords(n: number, spelling: OrdinalSpelling): string[] {
  if (n === 1000) {
    return ["milésimo"];
  }
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  const words: string[] = [];
  if (hundreds > 0) {
    words.push(ORD_HUNDREDS[hundreds]);
  }
  if (rest > 0) {
    words.push(...ordinalUnderHundred(rest, spelling));
  }
  return words;
}

function applyGender(words: string[], options: SpanishOrdinalOptions): string {
  const feminine = options.gender === "feminine";
  const out = words.map((w) => (feminine ? `${w.slice(0, -1)}a` : w));
  if (!feminine && options.beforeNoun) {
    const last = out.at(-1) ?? "";
    if (last.endsWith("primero") || last.endsWith("tercero")) {
      out[out.length - 1] = last.slice(0, -1);
    }
  }
  return out.join(" ");
}

function ordinalAbbreviation(
  n: number,
  words: string,
  options: SpanishOrdinalOptions
): string {
  if (options.gender === "feminine") {
    return `${n}.ª`;
  }
  const apocopated =
    options.beforeNoun &&
    (words.endsWith("primer") || words.endsWith("tercer"));
  return apocopated ? `${n}.er` : `${n}.º`;
}

/** The ordinal for 1–1000, or null outside that range. */
export function spanishOrdinal(
  n: number,
  options: SpanishOrdinalOptions
): SpanishOrdinal | null {
  if (!Number.isInteger(n) || n < 1 || n > MAX_ORDINAL) {
    return null;
  }
  const words = applyGender(ordinalWords(n, options.spelling), options);
  const alternatives: string[] = [];
  const otherSpelling: OrdinalSpelling =
    options.spelling === "oneWord" ? "twoWords" : "oneWord";
  const other = applyGender(ordinalWords(n, otherSpelling), options);
  if (other !== words) {
    alternatives.push(other);
  }
  if (n % 100 === 11 || n % 100 === 12) {
    const unit = n % 100 === 11 ? 1 : 2;
    const joined = applyGender(
      [
        ...(n >= 100 ? [ORD_HUNDREDS[Math.floor(n / 100)]] : []),
        joinedOrdinal(1, unit),
      ],
      options
    );
    if (joined !== words) {
      alternatives.push(joined);
    }
  }
  return {
    words,
    abbreviation: ordinalAbbreviation(n, words, options),
    alternatives,
  };
}
